import * as R from 'ramda'
import {indexedMap} from '../../../Data/ArrayHelper'
import {ProductShippingServiceForOrder} from '../../../Entities/ProductShippingServiceForOrder'
import {
    OpenPositionRow,
    OpenPositionRowData,
    OpenPositionRowValidation,
    ParcelsTab,
    ParcelWithPositionsData,
    ProductShippingServiceSection
} from './ParcelsState'
import {ParcelsSubAction} from './ParcelsActions'
import {OrderDetailsFeedback, SendRequest} from '../OrderDetailsFeedback'
import {update} from '../../../Data/ObjectHelper'
import * as Result from '../../../Data/Result'
import * as Nullable from '../../../Data/Nullable'
import {
    mandatoryNullableValidator,
    mandatoryStringValidator,
    maximumValidator,
    minimumValidator,
    nonemptyArrayValidator,
    stringToIntValidator,
    validateInputField
} from '../../../Data/FormValidation'
import * as ParcelResource from '../../../Request/ParcelResource'
import {decodeHttpResponse} from '../../../Data/Http'
import {
    ParcelWithDetails,
    parcelWithPositionsDecoder
} from '../../../Entities/ParcelWithDetails'
import {focus} from '../../../Data/Dom'
import {OpenPosition} from '../../../Entities/OpenPosition'
import {ParcelPositionWithPosition} from '../../../Entities/ParcelPositionWithPosition'
import {FeedbackLoop} from '../../../Data/FeedbackLoop'
import {OrderDetailsAction} from '../OrderDetailsActions'

export function initParcels(productShippingServicesForOrder: ProductShippingServiceForOrder[]): ParcelsTab {
    return {
        sections: R.map(
            (productShippingServiceForOrder): ProductShippingServiceSection => ({
                productShippingService:
                productShippingServiceForOrder.productShippingService,
                openPositionRows: sortOpenPositionRows(R.map(
                    initOpenPositionRow,
                    productShippingServiceForOrder.openPositions)),
                parcels: productShippingServiceForOrder.parcelsWithDetails,
                formState: {
                    type: 'Locked',
                },
                input: {
                    parcelDispatcherId: null,
                    trackingNumber: '',
                },
                validation: {
                    parcelDispatcherId: null,
                    trackingNumber: null,
                    positions: null
                },
                submissionAttempted: false
            }),
            productShippingServicesForOrder),
    }
}

function initOpenPositionRow(openPosition: OpenPosition): OpenPositionRow {
    return {
        position: openPosition.position,
        input: {
            selected: true,
            amount: openPosition.remainingAmount.toString()
        },
        validation: {
            amount: null
        },
        remainingAmount: openPosition.remainingAmount,
    }
}

type ParcelsLoop =
    FeedbackLoop<ParcelsTab, OrderDetailsFeedback>

export function parcelsReducer(
    state: ParcelsTab,
    action: ParcelsSubAction
): ParcelsLoop {
    switch (action.type) {
        case 'OpenPositionsSelectionChanged': {
            return {
                state: updateSection(
                    section => {
                        const inputUpdated = update({
                            openPositionRows:
                                indexedMap(
                                    (index, row) => update({
                                        input: update({
                                            selected:
                                                action.selected === 'all' ||
                                                R.contains(index, action.selected)
                                        }, row.input),
                                    }, row),
                                    section.openPositionRows),
                        }, section)
                        return update({
                            validation: update({
                                positions:
                                    validateInputField(
                                        sectionValidators.positions,
                                        inputUpdated.submissionAttempted,
                                        R.filter(
                                            row => row.input.selected,
                                            inputUpdated.openPositionRows))
                            }, inputUpdated.validation),
                        }, inputUpdated)
                    },
                    action.productShippingServiceId,
                    state
                ),
                feedback: [],
            }
        }
        case 'OpenPositionAmountChanged':
            return {
                state: updateSection(
                    section => updatePositionRow(
                        row =>
                            update({
                                input: update({
                                    amount: action.value,
                                }, row.input),
                                validation: update({
                                    amount:
                                        validateInputField(
                                            rowValidators.amount(
                                                row.remainingAmount),
                                            section.submissionAttempted,
                                            action.value),
                                }, row.validation),
                            }, row),
                        action.id,
                        section),
                    action.productShippingServiceId, state),
                feedback: [],
            }
        case 'ParcelDispatcherIdChanged':
            return {
                state: updateSection(
                    section => update({
                        input: update({
                            parcelDispatcherId: action.value,
                        }, section.input),
                        validation: update({
                            parcelDispatcherId:
                                validateInputField<number | null, 'Mandatory' | null, number>(
                                    sectionValidators.parcelDispatcherId,
                                    section.submissionAttempted,
                                    action.value),
                        }, section.validation),
                    }, section),
                    action.productShippingServiceId,
                    state),
                feedback: [],
            }
        case 'TrackingNumberChanged':
            return {
                state: updateSection(
                    section => update({
                        input: update({
                            trackingNumber: action.value,
                        }, section.input),
                        validation: update({
                            trackingNumber:
                                validateInputField(
                                    sectionValidators.trackingNumber,
                                    section.submissionAttempted,
                                    action.value),
                        }, section.validation),
                    }, section),
                    action.productShippingServiceId,
                    state),
                feedback: [],
            }
        case 'SubmitParcel': {
            const currentSection =
                state.sections.find(
                    section => section.productShippingService.id === action.productShippingServiceId)
            if (typeof currentSection === 'undefined') {
                return {state, feedback: []}
            }

            const validationOutcome =
                validateProductShippingServiceSection(currentSection)
            return {
                state: updateSection(
                    R.always(
                        update({
                            submissionAttempted: validationOutcome === null,
                        }, validationOutcome.section)),
                    action.productShippingServiceId,
                    state,
                ),
                feedback:
                    validationOutcome.data !== null ?
                        [{
                            type: 'SendRequest',
                            identifier: {
                                type: 'ParcelCreate',
                                productShippingServiceId:
                                action.productShippingServiceId,
                            },
                            httpRequest: ParcelResource.create(
                                validationOutcome.data,
                            ),
                        }] :
                        [],
            }
        }
        case 'ParcelCreateResponse': {
            const result =
                decodeHttpResponse(
                    parcelWithPositionsDecoder(),
                    action.result)
            if (result.type === 'Ok') {
                const currentSection =
                    R.find(
                        section =>
                            section.productShippingService.id === action.productShippingServiceId,
                        state.sections)
                if (typeof currentSection === 'undefined') {
                    return {state, feedback: []}
                }
                const newSection = processParcelCreation(result.value, currentSection)
                const newState = updateSection(
                    R.always(newSection),
                    action.productShippingServiceId,
                    state)
                return {
                    state: newState,
                    feedback: newSection.openPositionRows.length > 0 ?
                        [{
                            type: 'RunEffect',
                            effect:
                                focus<OrderDetailsAction>(
                                    `tracking-number-input-${newSection.productShippingService.id}`,
                                    {type: 'ParcelsAction', subAction: {type: 'NoOp'}},
                                )
                        }] :
                        [],
                }
            } else {
                // tslint:disable-next-line
                console.error(result.error)
                return {
                    state,
                    feedback: [],
                }
            }
        }
        case 'NoOp':
            return {state, feedback: []}
        case 'DeleteParcel':
            const feedBackRequest: SendRequest = {
                type: 'SendRequest',
                identifier: {
                    type: 'ParcelDelete',
                    parcelId: action.parcelId,
                    productShippingServiceId: action.productShippingServiceId
                },
                httpRequest: ParcelResource.delete_(action.parcelId)
            }
            return {state, feedback: [feedBackRequest]}
        case 'ParcelDeleteResponse':
            if (action.result.type === 'Err') {
                return {state, feedback: []}
            }
            return {
                state: updateSection((section: ProductShippingServiceSection) => {
                        const currentParcel = section.parcels.find(
                            parcel => parcel.parcel.id === action.parcelId)
                        if (typeof currentParcel === 'undefined') {
                            return section
                        }
                        const newOpenPositionRows = putBackOpenPositionRows(
                            currentParcel.parcelPositionsWithPosition,
                            section.openPositionRows
                        )
                        return update(
                            {
                                parcels: R.filter(
                                    (parcel: ParcelWithDetails) =>
                                        parcel.parcel.id !== action.parcelId,
                                    section.parcels),
                                openPositionRows: newOpenPositionRows,
                            },
                            section)
                    },
                    action.productShippingServiceId,
                    state),
                feedback: []
            }
    }
}

function appendNewParcel(
    parcel: ParcelWithDetails,
    section: ProductShippingServiceSection
): ProductShippingServiceSection {
    return update({
        parcels:
            R.append(
                parcel,
                section.parcels)
    }, section)
}

function subtractParcelFromOpenPositions(
    parcel: ParcelWithDetails,
    section: ProductShippingServiceSection
): ProductShippingServiceSection {
    return update({
        openPositionRows:
            R.filter(
                openPositionRow =>
                    openPositionRow.remainingAmount > 0,
                R.map(
                    openPositionRow2 =>
                        R.reduce(
                            (previousRow, parcelPosition) =>
                                update({
                                    remainingAmount: previousRow.remainingAmount - parcelPosition.parcelPosition.amount,
                                }, previousRow),
                            openPositionRow2,
                            R.filter(
                                parcelPosition =>
                                    parcelPosition.position.id === openPositionRow2.position.id,
                                parcel.parcelPositionsWithPosition)),
                    section.openPositionRows)),
    }, section)
}

function resetForm(section: ProductShippingServiceSection): ProductShippingServiceSection {
    return update({
        input: update({
            trackingNumber: '',
        }, section.input),
        submissionAttempted: false,
    }, section)
}

function updatePositionRow(
    fn: (positionRow: OpenPositionRow) => OpenPositionRow,
    id: number,
    section: ProductShippingServiceSection
): ProductShippingServiceSection {
    return update({
        openPositionRows:
            R.map(
                positionRow =>
                    positionRow.position.id === id ?
                        fn(positionRow) :
                        positionRow,
                section.openPositionRows),
    }, section)
}

function updateSection(
    fn: (section: ProductShippingServiceSection) => ProductShippingServiceSection,
    productShippingServiceId: number,
    state: ParcelsTab
): ParcelsTab {
    return update({
        sections:
            R.map(
                section =>
                    section.productShippingService.id === productShippingServiceId ?
                        fn(section) :
                        section,
                state.sections),
    }, state)
}

const sectionValidators = {
    parcelDispatcherId: mandatoryNullableValidator(),
    trackingNumber: mandatoryStringValidator(),
    positions: nonemptyArrayValidator(),
}

function validateProductShippingServiceSection(section: ProductShippingServiceSection): {
    section: ProductShippingServiceSection,
    data: ParcelWithPositionsData | null,
} {
    const
        validationResults = {
            parcelDispatcherId: sectionValidators.parcelDispatcherId(
                section.input.parcelDispatcherId),
            trackingNumber: sectionValidators.trackingNumber(
                section.input.trackingNumber),
            positions: sectionValidators.positions(
                R.filter(
                    row => row.input.selected,
                    section.openPositionRows))
        },

        sectionValidation = {
            parcelDispatcherId: Result.errorToNullable(validationResults.parcelDispatcherId),
            trackingNumber: Result.errorToNullable(validationResults.trackingNumber),
            positions: Result.errorToNullable(validationResults.positions),
        },

        openPositionsOutcome =
            validateOpenPositionRows(section.openPositionRows),

        data =
            validationResults.parcelDispatcherId.type === 'Ok' &&
            validationResults.trackingNumber.type === 'Ok' &&
            validationResults.positions.type === 'Ok' &&
            openPositionsOutcome.data !== null ?
                {
                    parcel: {
                        parcelDispatcherId: validationResults.parcelDispatcherId.value,
                        trackingNumber: validationResults.trackingNumber.value,
                    },
                    positions: openPositionsOutcome.data,
                } :
                null
    return {
        section: update(
            {
                validation: sectionValidation,
                openPositionRows: openPositionsOutcome.openPositionRows,
            },
            section
        ),
        data,
    }
}

interface OpenPositionRowValidationOutcome {
    openPositionRows: OpenPositionRow[],
    data: OpenPositionRowData[] | null,
}

function validateOpenPositionRows(openPositionRows: OpenPositionRow[]): OpenPositionRowValidationOutcome {
    return R.reduce(
        (previous: OpenPositionRowValidationOutcome, openPositionRow) => {
            const validationResult = validateOpenPositionRow(openPositionRow)
            switch (validationResult.type) {
                case 'Ok':
                    return {
                        openPositionRows:
                            R.append(
                                update({
                                    validation: {
                                        amount: null,
                                    }
                                }, openPositionRow),
                                previous.openPositionRows),
                        data:
                            openPositionRow.input.selected ?
                                Nullable.map(
                                    data =>
                                        R.append(
                                            validationResult.value,
                                            data),
                                    previous.data) :
                                previous.data,
                    }
                case 'Err':
                    return {
                        openPositionRows:
                            R.append(
                                update({
                                    validation: validationResult.error,
                                }, openPositionRow),
                                previous.openPositionRows),
                        data:
                            openPositionRow.input.selected ?
                                null :
                                previous.data,
                    }
            }
        },
        {openPositionRows: [], data: []},
        openPositionRows)
}

const rowValidators = {
    amount:
        (atMost: number) =>
            (value: string) =>
                Result.andThen(
                    maximumValidator(atMost),
                    Result.andThen(
                        minimumValidator(1),
                        Result.andThen(
                            stringToIntValidator(),
                            mandatoryStringValidator()(value))))
}

function validateOpenPositionRow(
    openPositionRow: OpenPositionRow,
): Result.Result<OpenPositionRowValidation, OpenPositionRowData> {
    const validation = {
        amount: rowValidators.amount(
            openPositionRow.remainingAmount)(openPositionRow.input.amount),
    }
    if (validation.amount.type === 'Ok') {
        return {
            type: 'Ok',
            value: {
                positionId: openPositionRow.position.id,
                amount: validation.amount.value,
            }
        }
    } else {
        return {
            type: 'Err',
            error: {
                amount: Result.errorToNullable(validation.amount),
            }
        }
    }
}

const sortOpenPositionRows = R.sortBy(
    R.compose(
        R.toLower,
        (openPositionRow: OpenPositionRow) =>
            openPositionRow.position.name))

export function processParcelCreation(
    resultParcel: ParcelWithDetails,
    currentSection: ProductShippingServiceSection
): ProductShippingServiceSection {
    return resetForm(
        subtractParcelFromOpenPositions(
            resultParcel,
            appendNewParcel(
                resultParcel,
                currentSection)))
}

export function putBackOpenPositionRows(
    addToOpenPositionRows: ParcelPositionWithPosition[],
    openPositionRows: OpenPositionRow[]
): OpenPositionRow[] {
    return sortOpenPositionRows(R.reduce(
        (previous, parcelPosition: ParcelPositionWithPosition) =>
            R.any(
                row => row.position.id === parcelPosition.parcelPosition.positionId, previous)
                ? R.map(
                row =>
                    row.position.id === parcelPosition.parcelPosition.positionId
                        ? update({
                            input: update({
                                    selected: true,
                                    amount: parcelPosition.parcelPosition.amount.toString()
                                },
                                row.input),
                            remainingAmount: row.remainingAmount + parcelPosition.parcelPosition.amount
                        }, row) : row,
                previous)
                : R.append(
                initOpenPositionRow(
                    {
                        position: parcelPosition.position,
                        remainingAmount: parcelPosition.parcelPosition.amount
                    }),
                previous),
        openPositionRows,
        addToOpenPositionRows))
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderDetails/Parcels/ParcelsReducer.ts